import { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Chip,
  Collapse,
  ListItemIcon
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as GuardiaLogo } from '../../assets/logo_guardia.svg';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ThemeContext } from '../Context';
import { ScrollContext } from '../Context';
import { GuardiaAccessChecker } from './GuardiaAccessChecker';
import axios from 'axios';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';

function Rubrics() {
  const [rubrics, setRubrics] = useState([]);
  const [filteredRubrics, setFilteredRubrics] = useState([]);
  const [categories, setCategories] = useState(['Toutes les rubriques (ordre logique)', 'Toutes les rubriques (ordre alphabétique)', 'Mes rubriques']);
  const [selectedCategory, setSelectedCategory] = useState('Toutes les rubriques (ordre logique)');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRubrics, setUserRubrics] = useState([]);
  const [userRubricsLoading, setUserRubricsLoading] = useState(false);
  const [userRubricsError, setUserRubricsError] = useState('');
  const [rubricCounts, setRubricCounts] = useState({});
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { theme } = useContext(ThemeContext);
  const { triggerScrollToBottom } = useContext(ScrollContext);
  const [newRubricTitle, setNewRubricTitle] = useState('');
  const [isAddingRubric, setIsAddingRubric] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [guardiaAccess, setGuardiaAccess] = useState({ hasAccess: false, reason: '' });
  const lastActionRef = useRef({
    type: null,
    id: null,
    timestamp: null
  });
  const [emptyRubricExpanded, setEmptyRubricExpanded] = useState(true);
  const [templateRubricExpanded, setTemplateRubricExpanded] = useState(true);
  const [emptyRubricTitleError, setEmptyRubricTitleError] = useState(false);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Fonction pour calculer le nombre d'occurrences de chaque rubrique
  const calculateRubricCounts = (reportRubrics, templateRubrics) => {
    const counts = {};
    
    templateRubrics.forEach(templateRubric => {
      const count = reportRubrics.filter(reportRubric => {
        // Comparer d'abord par version si elle existe
        if (templateRubric.version && reportRubric.version) {
          return reportRubric.version === templateRubric.version;
        }
        // Fallback sur le titre pour les rubriques sans version
        return reportRubric.title === templateRubric.title ||
               reportRubric.title === templateRubric['content-title'];
      }).length;
      counts[templateRubric.id] = count;
    });
    
    return counts;
  };

  // Fonction pour mettre à jour les compteurs depuis les données du rapport
  const updateRubricCounts = async () => {
    try {
      if (!report) {
        setRubricCounts({});
        return;
      }
      
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/report/search`, {
        params: { number: report.number, year: report.year },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data && response.data.rubrics) {
        const counts = calculateRubricCounts(response.data.rubrics, rubrics);
        setRubricCounts(counts);
      }
    } catch (error) {
      console.error('Erreur lors du calcul des compteurs de rubriques:', error);
    }
  };

  useEffect(() => {
    fetchRubrics();
  }, []);

  useEffect(() => {
    filterRubrics();
  }, [searchQuery, selectedCategory, rubrics, userRubrics]);

  useEffect(() => {
    if (selectedCategory === 'Mes rubriques') {
      fetchUserRubrics();
    }
  }, [selectedCategory, user]);

  // Nouveau useEffect pour mettre à jour les compteurs quand les rubriques ou le rapport changent
  useEffect(() => {
    updateRubricCounts();
  }, [rubrics, report]);

  // Vérifier l'accès GuardIA au chargement
  useEffect(() => {
    const checkGuardiaAccess = async () => {
      const access = await GuardiaAccessChecker.checkFeatureAccess('write');
      setGuardiaAccess(access);
    };

    checkGuardiaAccess();
  }, []);

  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdated = (updatedReport) => {
      if (updatedReport.number === report.number && 
          updatedReport.year === report.year &&
          updatedReport.rubrics) {
            
        // Vérifier si cette mise à jour ne provient pas de cette instance
        const lastAction = lastActionRef.current;
        const now = Date.now();
        
        // Réduire la fenêtre de temps et vérifier aussi l'ID pour plus de précision
        if (lastAction.type === 'add_rubric' && 
            now - lastAction.timestamp < 1000 && 
            updatedReport.rubrics.some(r => r.id === lastAction.id)) {
          return; // Ignorer les mises à jour si elles proviennent probablement de cette instance
        }
        
        // Mettre à jour les compteurs avec les nouvelles données
        const counts = calculateRubricCounts(updatedReport.rubrics, rubrics);
        setRubricCounts(counts);
        
        // Recharger les données du rapport pour avoir les rubriques à jour
        fetchReportData();
      }
    };

    // Écouter aussi les événements directs d'updateReport
    const handleDirectUpdate = (data) => {
      if (data.reportId === `${report.number}-${report.year}` && data.rubrics) {
        // Vérifier si cette mise à jour ne provient pas de cette instance
        const lastAction = lastActionRef.current;
        const now = Date.now();
        
        // Réduire la fenêtre de temps et vérifier aussi l'ID pour plus de précision
        if (lastAction.type === 'add_rubric' && 
            now - lastAction.timestamp < 1000 && 
            data.rubrics.some(r => r.id === lastAction.id)) {
          return; // Ignorer les mises à jour si elles proviennent probablement de cette instance
        }
        
        // Mettre à jour les compteurs avec les nouvelles données
        const counts = calculateRubricCounts(data.rubrics, rubrics);
        setRubricCounts(counts);
        
        // Recharger les données du rapport
        fetchReportData();
      }
    };

    socket.on('reportUpdated', handleReportUpdated);
    socket.on('updateReport', handleDirectUpdate);

    // *** SUPPRIMÉ : Plus de joinReport/leaveReport individuel - géré par Core.js ***

    return () => {
      socket.off('reportUpdated', handleReportUpdated);
      socket.off('updateReport', handleDirectUpdate);
      // *** SUPPRIMÉ : Plus de joinReport/leaveReport individuel - géré par Core.js ***
    };
  }, [socket, report, rubrics]);

  const fetchUserRubrics = async () => {
    try {
      if (!user) {
        setUserRubrics([]);
        return;
      }

      setUserRubricsLoading(true);
      setUserRubricsError('');

      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/user/${user}/rubrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const rawRubrics = Array.isArray(response.data?.myRubrics) ? response.data.myRubrics : [];
      const normalized = rawRubrics.map((rubric) => ({
        id: `user-rubric-${rubric.name}`,
        title: rubric.name,
        content: rubric.content || '',
        version: '',
        type: 'user',
        isUserRubric: true
      }));

      normalized.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
      setUserRubrics(normalized);
    } catch (err) {
      console.error('Erreur lors du chargement des rubriques utilisateur:', err);
      setUserRubricsError('Erreur lors du chargement de vos rubriques');
    } finally {
      setUserRubricsLoading(false);
    }
  };

  const fetchRubrics = async () => {
    try {
      const response = await fetch('/api/standards/rubrics');
      const data = await response.json();
      
      // Filtrer les rubriques non obsolètes
      const activeRubrics = data.filter(rubric => !rubric.obsolete);
      
      // Extraire toutes les catégories uniques
      const allCategories = new Set(['Toutes les rubriques (ordre logique)', 'Toutes les rubriques (ordre alphabétique)', 'Mes rubriques']);
      activeRubrics.forEach(rubric => {
        if (rubric.categories) {
          rubric.categories.forEach(category => allCategories.add(category));
        }
      });

      setRubrics(activeRubrics);
      setCategories(Array.from(allCategories));
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des rubriques');
      setLoading(false);
    }
  };

  const fetchReportData = async () => {
    try {
      if (!report) return;
      
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/report/search`, {
        params: { number: report.number, year: report.year },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Mise à jour des données si nécessaire
      if (response.data) {
        console.log('Données du rapport rechargées:', response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données du rapport:', error);
    }
  };

  const filterRubrics = () => {
    const isMyRubrics = selectedCategory === 'Mes rubriques';
    let filtered = isMyRubrics ? [...userRubrics] : [...rubrics];

    // Filtre par catégorie
    if (
      selectedCategory !== 'Toutes les rubriques (ordre logique)' &&
      selectedCategory !== 'Toutes les rubriques (ordre alphabétique)' &&
      selectedCategory !== 'Mes rubriques'
    ) {
      filtered = filtered.filter(rubric => 
        rubric.categories && rubric.categories.includes(selectedCategory)
      );
    }

    // Filtre par recherche (titre et mots-clés)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(rubric => {
        // Recherche dans le titre
        const titleMatch = rubric.title.toLowerCase().includes(query);
        
        // Recherche dans les mots-clés
        const keywordMatch = rubric.keywords && rubric.keywords.some(keyword =>
          keyword.toLowerCase().includes(query)
        );
        
        return titleMatch || keywordMatch;
      });
    }

    // Tri selon l'option sélectionnée
    if (selectedCategory === 'Toutes les rubriques (ordre alphabétique)' || isMyRubrics) {
      filtered.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
    }
    // Pour 'Toutes les rubriques (ordre logique)', on garde l'ordre original (pas de tri)

    setFilteredRubrics(filtered);
  };

  const handleCloseNotification = () => {
    setNotification({...notification, open: false});
  };

  const handleGuardiaGeneration = async () => {
    if (!guardiaAccess.hasAccess) {
      alert(guardiaAccess.reason);
      return;
    }

    // Vérification qu'un rapport est ouvert
    if (!report) {
      setNotification({
        open: true,
        message: 'Aucun rapport ouvert. Veuillez ouvrir un rapport avant d\'ajouter une rubrique.',
        severity: 'error'
      });
      return;
    }

    try {
      // Récupérer les données actuelles du rapport
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/report/search`, {
        params: { number: report.number, year: report.year },
        headers: { Authorization: `Bearer ${token}` },
      });

      const reportData = response.data;
      const currentRubrics = reportData.rubrics || [];

      // Créer une nouvelle rubrique GuardIA
      const newRubric = {
        id: new Date().getTime().toString(),
        title: 'Rubrique GuardIA',
        content: '',
        position: currentRubrics.length + 1,
        generated: false,
        isEditing: false,
        editedBy: null,
        isGuardia: true
      };

      // Ajouter la nouvelle rubrique à la liste
      const updatedRubrics = [...currentRubrics, newRubric];

      // Mettre à jour via API
      await axios.put(
        `/api/report/${reportData._id}/rubrics`,
        { rubrics: updatedRubrics },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics: updatedRubrics
        });

        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics: updatedRubrics
        });
      }

      // Notification de succès
      setNotification({
        open: true,
        message: 'Rubrique GuardIA ajoutée avec succès.',
        severity: 'success'
      });

      // Log de l'ajout de rubrique GuardIA
      logAction(LOG_ACTIONS.RUBRIC_ADD_GUARDIA, formatReportNumber(report.number, report.year), {
        rubricId: newRubric.id,
        title: newRubric.title
      });

      // Scroll vers le bas pour afficher la nouvelle rubrique
      triggerScrollToBottom();

    } catch (error) {
      console.error('Erreur lors de l\'ajout de la rubrique GuardIA:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de l\'ajout de la rubrique GuardIA.',
        severity: 'error'
      });
    }
  };

  const handleAddEmptyRubric = async () => {
    // Validation du titre (champ obligatoire)
    if (!newRubricTitle.trim()) {
      setEmptyRubricTitleError(true);
      setNotification({
        open: true,
        message: 'Le nom de la rubrique est obligatoire',
        severity: 'error'
      });
      return;
    }
    
    // Réinitialiser l'erreur si le titre est valide
    setEmptyRubricTitleError(false);

    // Vérification qu'un rapport est ouvert
    if (!report) {
      setNotification({
        open: true,
        message: 'Aucun rapport n\'est actuellement ouvert',
        severity: 'error'
      });
      return;
    }

    try {
      setIsAddingRubric(true);
      const token = sessionStorage.getItem('authToken');
      
      // Récupérer les données actuelles du rapport
      const reportResponse = await axios.get(`/api/report/search`, {
        params: { number: report.number, year: report.year },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!reportResponse.data) {
        throw new Error('Impossible de récupérer les données du rapport');
      }
      
      const reportId = reportResponse.data._id;
      const currentRubrics = reportResponse.data.rubrics || [];
      
      // Créer la nouvelle rubrique avec les caractéristiques demandées
      const newRubric = {
        id: `rubric-${Date.now()}-${Math.floor(Math.random() * 10000)}`, 
        position: currentRubrics.length + 1,
        title: newRubricTitle,
        content: "",
        generated: true,
        isEditing: false,
        editedBy: null
      };
      
      // Ajouter la nouvelle rubrique à la liste existante
      const updatedRubrics = [...currentRubrics, newRubric];
      
      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/rubrics`,
        { rubrics: updatedRubrics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Mettre à jour immédiatement les compteurs localement
      const updatedCounts = calculateRubricCounts(updatedRubrics, rubrics);
      setRubricCounts(updatedCounts);
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        // Émettre deux événements pour s'assurer que les mises à jour sont reçues
        // 1. updateReport : pour les clients qui écoutent cet événement
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics: updatedRubrics
        });
        
        // 2. Demander au serveur de diffuser reportUpdated à tous les clients
        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics: updatedRubrics
        });
      }
      
      // Enregistrer l'action pour éviter les doublons de traitement
      lastActionRef.current = {
        type: 'add_rubric',
        id: newRubric.id,
        timestamp: Date.now()
      };
      
      // Réinitialiser le champ
      setNewRubricTitle('');
      
      // Notification de succès
      setNotification({
        open: true,
        message: 'Rubrique ajoutée avec succès',
        severity: 'success'
      });

      // Log de l'ajout de rubrique vierge
      logAction(LOG_ACTIONS.RUBRIC_ADD_EMPTY, formatReportNumber(report.number, report.year), {
        rubricId: newRubric.id,
        title: newRubricTitle
      });

      // Scroll vers le bas pour afficher la nouvelle rubrique
      triggerScrollToBottom();
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la rubrique:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de l\'ajout de la rubrique',
        severity: 'error'
      });
    } finally {
      setIsAddingRubric(false);
    }
  };

  const handleAddTemplateRubric = async (rubric) => {
    // Vérification qu'un rapport est ouvert
    if (!report) {
      setNotification({
        open: true,
        message: 'Aucun rapport n\'est actuellement ouvert',
        severity: 'error'
      });
      return;
    }

    try {
      setIsAddingRubric(true);
      const token = sessionStorage.getItem('authToken');
      
      // Récupérer les données actuelles du rapport
      const reportResponse = await axios.get(`/api/report/search`, {
        params: { number: report.number, year: report.year },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!reportResponse.data) {
        throw new Error('Impossible de récupérer les données du rapport');
      }
      
      const reportId = reportResponse.data._id;
      const currentRubrics = reportResponse.data.rubrics || [];
      
      // Créer la nouvelle rubrique selon le type (ou rubrique personnelle)
      let newRubric;

      if (rubric.isUserRubric) {
        newRubric = {
          id: `rubric-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
          position: currentRubrics.length + 1,
          title: rubric.title,
          content: rubric.content || "",
          generated: true,
          isEditing: false,
          editedBy: null,
          version: ""
        };
      } else {
        newRubric = {
          id: `rubric-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
          position: currentRubrics.length + 1,
          title: rubric.type === "manual" ? (rubric['content-title'] || rubric.title) : rubric.title,
          isEditing: false,
          version: rubric.version || "",
          editedBy: null
        };
        
        // Appliquer les propriétés spécifiques selon le type
        if (rubric.type === "intelligent") {
          newRubric = {
            ...newRubric,
            content: "",
            generated: false
          };
        } else if (rubric.type === "manual") {
          newRubric = {
            ...newRubric,
            content: rubric.content || "",
            generated: true
          };
        } else {
          // Cas par défaut si type non spécifié
          newRubric = {
            ...newRubric,
            content: rubric.content || "",
            generated: true
          };
        }
      }
      
      // Ajouter la nouvelle rubrique à la liste existante
      const updatedRubrics = [...currentRubrics, newRubric];
      
      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/rubrics`,
        { rubrics: updatedRubrics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Mettre à jour immédiatement les compteurs localement
      const updatedCounts = calculateRubricCounts(updatedRubrics, rubrics);
      setRubricCounts(updatedCounts);
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        // Émettre deux événements pour s'assurer que les mises à jour sont reçues
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics: updatedRubrics
        });
        
        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics: updatedRubrics
        });
      }
      
      // Enregistrer l'action pour éviter les doublons de traitement
      lastActionRef.current = {
        type: 'add_rubric',
        id: newRubric.id,
        timestamp: Date.now()
      };
      
      // Notification de succès
      setNotification({
        open: true,
        message: 'Rubrique ajoutée avec succès',
        severity: 'success'
      });

      // Log de l'ajout de rubrique template
      const rubricType = rubric.isUserRubric ? 'personnelle' : (rubric.type === 'intelligent' ? 'standard intelligente' : 'standard manuelle');
      logAction(
        rubric.isUserRubric ? LOG_ACTIONS.RUBRIC_ADD_PERSONAL : LOG_ACTIONS.RUBRIC_ADD_TEMPLATE, 
        formatReportNumber(report.number, report.year), 
        {
          rubricId: newRubric.id,
          title: newRubric.title,
          type: rubricType,
          version: newRubric.version || null
        }
      );

      // Scroll vers le bas pour afficher la nouvelle rubrique
      triggerScrollToBottom();
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la rubrique:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de l\'ajout de la rubrique',
        severity: 'error'
      });
    } finally {
      setIsAddingRubric(false);
    }
  };

  const isMyRubricsSelected = selectedCategory === 'Mes rubriques';
  const currentError = isMyRubricsSelected ? (userRubricsError || error) : error;
  const isCurrentLoading = isMyRubricsSelected ? userRubricsLoading : loading;

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
              backgroundColor: isDark ? '#1a252f' : 'inherit'
    }}>
      <Box sx={{ p: 0.5, mb: -0.7 }}>
        <Box 
          onClick={() => setEmptyRubricExpanded(!emptyRubricExpanded)}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            pl: 1,
            pr: 0.5,
            '&:hover': {
              bgcolor: isDark ? 'rgba(79, 195, 247, 0.05)' : 'rgba(0, 0, 0, 0.02)'
            }
          }}
        >
          <Typography variant="caption" sx={{ 
            fontSize: '0.8rem', 
            fontWeight: 'bold',
            color: isDark ? '#4fc3f7' : 'inherit',
            flex: 1
          }}>
            Ajouter une rubrique vierge
          </Typography>
          <ExpandMoreIcon 
            sx={{ 
              fontSize: '1rem',
              color: isDark ? '#4fc3f7' : 'inherit',
              transform: emptyRubricExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.3s'
            }} 
          />
        </Box>
        <Collapse in={emptyRubricExpanded}>
        <List dense disablePadding sx={{ mt: 0, pt: 0 }}>
          <ListItem
            dense
            sx={{
              py: 0.5,
              borderBottom: '1px solid',
              borderColor: isDark ? '#2d3f4c' : 'divider',
              '&:hover': {
                bgcolor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'action.hover',
              }
            }}
          >
            <TextField
              size="small"
              fullWidth
              variant="standard"
              placeholder="Nom de la rubrique"
              value={newRubricTitle}
              onChange={(e) => {
                setNewRubricTitle(e.target.value);
                // Réinitialiser l'erreur dès que l'utilisateur tape
                if (emptyRubricTitleError) {
                  setEmptyRubricTitleError(false);
                }
              }}
              error={emptyRubricTitleError}
              helperText={emptyRubricTitleError ? "Ce champ est obligatoire" : ""}
              margin="dense"
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '0.8rem',
                  backgroundColor: 'transparent',
                  color: isDark ? '#ecf0f1' : 'inherit',
                  '&:before': {
                    borderBottomColor: isDark ? '#2d3f4c' : undefined
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottomColor: isDark ? '#4fc3f7' : undefined
                  },
                  '&:after': {
                    borderBottomColor: isDark ? '#4fc3f7' : undefined
                  }
                },
                '& .MuiInputBase-input': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  py: 0.5,
                  color: isDark ? '#ecf0f1' : 'inherit'
                },
                '& .MuiFormHelperText-root': {
                  margin: 0,
                  fontSize: '0.7rem',
                  color: isDark ? '#95a5a6' : 'inherit'
                }
              }}
            />
            <ListItemSecondaryAction>
              <IconButton 
                edge="end" 
                color="primary"
                size="small"
                onClick={handleAddEmptyRubric}
                disabled={isAddingRubric}
                sx={{ padding: 0.5 }}
              >
                {isAddingRubric ? <CircularProgress size={16} /> : <AddCircleOutlineIcon fontSize="small" />}
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
          
          {/* Élément pour la génération avec GuardIA */}
          {false && (
            <ListItem
              dense
              sx={{
                py: 1,
                borderBottom: '1px solid',
                borderColor: isDark ? '#2d3f4c' : 'divider',
                backgroundColor: isDark ? 'rgba(128, 0, 128, 0.05)' : 'rgba(128, 0, 128, 0.02)',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(128, 0, 128, 0.1)' : 'rgba(128, 0, 128, 0.05)',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <GuardiaLogo
                  style={{
                    width: '20px',
                    height: '20px',
                    color: isDark ? '#c084fc' : '#800080'
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: isDark ? '#ecf0f1' : 'inherit',
                    flex: 1
                  }}
                >
                  Générer avec GuardIA
                </Typography>
              </Box>
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  color="primary"
                  size="small"
                  onClick={handleGuardiaGeneration}
                  disabled={!guardiaAccess.hasAccess}
                  sx={{
                    padding: 0.5,
                    color: guardiaAccess.hasAccess ? (isDark ? '#c084fc' : '#800080') : (isDark ? '#555' : '#ccc'),
                    '&:hover': {
                      bgcolor: guardiaAccess.hasAccess
                        ? (isDark ? 'rgba(128, 0, 128, 0.2)' : 'rgba(128, 0, 128, 0.1)')
                        : undefined
                    },
                    '&.Mui-disabled': {
                      color: isDark ? '#555' : '#ccc'
                    }
                  }}
                >
                  <AddCircleOutlineIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          )}
        </List>
        </Collapse>
      </Box>
      
      <Box sx={{ pt: 0, px: 0.5 }}>
        <Box 
          onClick={() => setTemplateRubricExpanded(!templateRubricExpanded)}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            pl: 1,
            pr: 0.5,
            '&:hover': {
              bgcolor: isDark ? 'rgba(79, 195, 247, 0.05)' : 'rgba(0, 0, 0, 0.02)'
            }
          }}
        >
          <Typography variant="caption" sx={{ 
            fontSize: '0.8rem', 
            fontWeight: 'bold',
            color: isDark ? '#4fc3f7' : 'inherit',
            flex: 1
          }}>
            Ajouter une rubrique générative
          </Typography>
          <ExpandMoreIcon 
            sx={{ 
              fontSize: '1rem',
              color: isDark ? '#4fc3f7' : 'inherit',
              transform: templateRubricExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.3s'
            }} 
          />
        </Box>
        <Collapse in={templateRubricExpanded}>
        <Box sx={{ 
          p: 0.5, 
          borderBottom: 1, 
          borderColor: isDark ? '#2d3f4c' : 'divider'
        }}>
          <FormControl fullWidth size="small" sx={{ mb: 0.5 }}>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            renderValue={(value) => {
              if (value === 'Toutes les rubriques (ordre logique)') {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PsychologyIcon fontSize="small" sx={{ color: isDark ? '#4fc3f7' : 'inherit' }} />
                    <span>{value}</span>
                  </Box>
                );
              }
              if (value === 'Toutes les rubriques (ordre alphabétique)') {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SortByAlphaIcon fontSize="small" sx={{ color: isDark ? '#4fc3f7' : 'inherit' }} />
                    <span>{value}</span>
                  </Box>
                );
              }
              if (value === 'Mes rubriques') {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarBorderIcon fontSize="small" sx={{ color: isDark ? '#fbc02d' : 'inherit' }} />
                    <span>{value}</span>
                  </Box>
                );
              }
              return value;
            }}
              sx={{ 
                height: 32, 
                fontSize: '0.8rem',
                backgroundColor: isDark ? '#22303e' : 'inherit',
                color: isDark ? '#ecf0f1' : 'inherit',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? '#2d3f4c' : undefined
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? '#4fc3f7' : undefined
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? '#4fc3f7' : undefined
                },
                '.MuiSelect-select': {
                  py: 0.5
                },
                '& .MuiSvgIcon-root': {
                  color: isDark ? '#ecf0f1' : 'inherit'
                }
              }}
            >
              {categories.map((category) => (
                <MenuItem 
                  key={category} 
                  value={category} 
                  sx={{ 
                    fontSize: '0.8rem', 
                    minHeight: 'auto', 
                    py: 0.5,
                    backgroundColor: isDark ? '#22303e' : 'inherit',
                    color: isDark ? '#ecf0f1' : 'inherit',
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : undefined
                    },
                    '&.Mui-selected': {
                      backgroundColor: isDark ? 'rgba(79, 195, 247, 0.2)' : undefined
                    }
                  }}
                >
                  {category === 'Toutes les rubriques (ordre logique)' && (
                    <ListItemIcon sx={{ minWidth: 28, color: isDark ? '#4fc3f7' : 'inherit' }}>
                      <PsychologyIcon fontSize="small" />
                    </ListItemIcon>
                  )}
                  {category === 'Toutes les rubriques (ordre alphabétique)' && (
                    <ListItemIcon sx={{ minWidth: 28, color: isDark ? '#4fc3f7' : 'inherit' }}>
                      <SortByAlphaIcon fontSize="small" />
                    </ListItemIcon>
                  )}
                  {category === 'Mes rubriques' && (
                    <ListItemIcon sx={{ minWidth: 28, color: isDark ? '#fbc02d' : 'inherit' }}>
                      <StarBorderIcon fontSize="small" />
                    </ListItemIcon>
                  )}
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher une rubrique..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ 
              '& .MuiInputBase-root': { 
                height: 32,
                fontSize: '0.8rem',
                backgroundColor: isDark ? '#22303e' : 'inherit',
                color: isDark ? '#ecf0f1' : 'inherit',
                '& fieldset': {
                  borderColor: isDark ? '#2d3f4c' : undefined
                },
                '&:hover fieldset': {
                  borderColor: isDark ? '#4fc3f7' : undefined
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDark ? '#4fc3f7' : undefined
                }
              },
              '& .MuiInputBase-input': {
                py: 0.5,
                color: isDark ? '#ecf0f1' : 'inherit'
              },
              '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                color: isDark ? '#95a5a6' : undefined
              }
            }}
          />
        </Box>

      {currentError && (
        <Alert 
          severity="error" 
          sx={{ 
            m: 0.5, 
            py: 0, 
            fontSize: '0.75rem',
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : undefined,
            color: isDark ? '#ff6b6b' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ff6b6b' : undefined
            }
          }}
        >
          {currentError}
        </Alert>
      )}

      {isCurrentLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <List 
          dense 
          disablePadding 
          sx={{ 
            flex: 1, 
            overflowY: 'auto',
            backgroundColor: isDark ? '#1a252f' : 'inherit',
            '& .MuiListItem-root': {
              py: 0
            },
            // Styles personnalisés pour les scrollbars en mode sombre
            '&::-webkit-scrollbar': {
              width: isDark ? '8px' : undefined,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: isDark ? '#22303e' : undefined,
              borderRadius: isDark ? '4px' : undefined,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: isDark ? '#2d3f4c' : undefined,
              borderRadius: isDark ? '4px' : undefined,
              '&:hover': {
                backgroundColor: isDark ? '#384550' : undefined,
              },
            },
            // Pour Firefox
            scrollbarWidth: isDark ? 'thin' : undefined,
            scrollbarColor: isDark ? '#2d3f4c #22303e' : undefined,
          }}
        >
          {filteredRubrics.map((rubric) => (
            <ListItem
              key={rubric.id}
              sx={{
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: isDark ? '#2d3f4c' : 'divider',
                minHeight: 32,
                backgroundColor: isDark ? '#22303e' : 'inherit',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'action.hover',
                }
              }}
            >
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                        flex: 1,
                        color: isDark ? '#ecf0f1' : 'inherit'
                      }}
                    >
                      {rubric.title}
                    </Typography>
                    {rubricCounts[rubric.id] > 0 && (
                      <Chip
                        label={rubricCounts[rubric.id]}
                        size="small"
                        color="primary"
                        sx={{ 
                          height: 16,
                          fontSize: '0.65rem',
                          minWidth: 20,
                          '& .MuiChip-label': {
                            px: 0.5
                          }
                        }}
                      />
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  color="primary"
                  size="small"
                  onClick={() => handleAddTemplateRubric(rubric)}
                  disabled={isAddingRubric}
                  sx={{ padding: 0.5 }}
                >
                  {isAddingRubric ? <CircularProgress size={16} /> : <AddCircleOutlineIcon fontSize="small" />}
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {filteredRubrics.length === 0 && !loading && (
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.75rem',
                  color: isDark ? '#95a5a6' : 'text.secondary'
                }}
              >
                {searchQuery 
                  ? 'Aucune rubrique trouvée' 
                  : selectedCategory !== 'Toutes les rubriques (ordre logique)' && selectedCategory !== 'Toutes les rubriques (ordre alphabétique)'
                    ? 'Aucune rubrique dans cette catégorie'
                    : 'Aucune rubrique disponible'
                }
              </Typography>
            </Box>
          )}
        </List>
      )}
        </Collapse>
      </Box>
      
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ 
            width: '100%', 
            fontSize: '0.8rem', 
            py: 0.5,
            backgroundColor: isDark 
              ? (notification.severity === 'success' 
                  ? 'rgba(76, 175, 80, 0.15)' 
                  : 'rgba(244, 67, 54, 0.15)')
              : undefined,
            color: isDark 
              ? (notification.severity === 'success' 
                  ? '#4ecdc4' 
                  : '#ff6b6b')
              : undefined,
            '& .MuiAlert-icon': {
              color: isDark 
                ? (notification.severity === 'success' 
                    ? '#4ecdc4' 
                    : '#ff6b6b')
                : undefined
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Rubrics;



